import { useState } from "react";
import * as Sentry from "@sentry/react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useRemoveIssueContributor } from "@/hooks/use-issue-mutations";
import { ApiError, OfflineError } from "@/lib/api";
import { personInitials, personName, seesEveryIssue } from "@/lib/issues";
import type { IssueContributor, ProjectDetail } from "@/types/api";

interface IssueContributorsProps {
  projectId: string;
  issueId: string;
  /** Oldest first, as the server sends them. */
  contributors: IssueContributor[];
  /** For "(you)" and "invited by you". */
  currentUserId: string | undefined;
  /** Picks the remove confirmation's wording. Absent while it loads. */
  project: ProjectDetail | undefined;
}

/**
 * Everyone invited onto an issue, who invited them, and Remove where the
 * server says the viewer may. The raiser and the assignee are never here; they
 * show in their own fields. See [[issue-contributors.spec]].
 */
export function IssueContributors({
  projectId,
  issueId,
  contributors,
  currentUserId,
  project,
}: IssueContributorsProps) {
  // Kept apart from `open` so the dialog still names its person while it
  // animates closed.
  const [target, setTarget] = useState<IssueContributor | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <section aria-labelledby="issue-contributors-heading" className="mt-6">
      <div className="mb-2 flex items-baseline gap-1.5">
        <h2 id="issue-contributors-heading" className="text-sm font-semibold">
          Contributors
        </h2>
        {contributors.length > 0 && (
          <span className="text-[13px] text-muted-foreground">
            {contributors.length}
          </span>
        )}
      </div>

      {contributors.length === 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-muted/40 p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
            <UserPlus className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <p className="text-sm font-medium text-muted-foreground">
            No contributors yet
          </p>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-border bg-card">
          {contributors.map((row) => {
            const name = personName(row.user);
            return (
              <li
                key={row.id}
                className="flex items-center gap-3 border-b border-border py-2.5 pr-1.5 pl-3 last:border-b-0"
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground"
                >
                  {personInitials(row.user)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {name}
                    {row.user.id === currentUserId && (
                      <span className="text-muted-foreground"> (you)</span>
                    )}
                  </p>
                  <p className="mt-px truncate text-xs text-muted-foreground">
                    {joinedLine(row, currentUserId)}
                  </p>
                </div>
                {/* Hidden, never disabled, where the server says no
                    ([[permissions]]). */}
                {row.can_remove && (
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    onClick={() => {
                      setTarget(row);
                      setConfirmOpen(true);
                    }}
                    className="h-11 shrink-0 rounded-md px-3 text-[13px] font-semibold text-foreground/80 transition-colors hover:bg-accent"
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          {/* Mounts only while open and is keyed on it, so a closed dialog
              costs the page none of its hooks, and an error from one attempt
              is gone by the next. */}
          {target && (
            <RemoveContributorBody
              key={confirmOpen ? "open" : "closed"}
              projectId={projectId}
              issueId={issueId}
              contributor={target}
              project={project}
              onRemoved={() => setConfirmOpen(false)}
            />
          )}
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function RemoveContributorBody({
  projectId,
  issueId,
  contributor,
  project,
  onRemoved,
}: {
  projectId: string;
  issueId: string;
  contributor: IssueContributor;
  project: ProjectDetail | undefined;
  onRemoved: () => void;
}) {
  const remove = useRemoveIssueContributor();
  const [error, setError] = useState<string | null>(null);
  const name = personName(contributor.user);

  // Someone whose roles show them every issue keeps seeing this one, so the
  // line says what removal actually does to them. Unknown reads as the
  // contributor wording: most people invited are contributors.
  const keepsSeeingIt = project
    ? seesEveryIssue(project, contributor.user.id)
    : null;
  const consequence = keepsSeeingIt
    ? "They're taken off this issue. Their updates stay."
    : "They lose access to this issue. Their updates stay.";

  const handleRemove = () => {
    if (remove.isPending) return;
    setError(null);
    remove.mutate(
      { projectId, issueId, userId: contributor.user.id },
      {
        onSuccess: () => {
          toast(`${name} removed`);
          onRemoved();
        },
        onError: (err) => {
          // Never `err.message`: Safari's "Load failed" is not something to
          // show a superintendent.
          if (err instanceof OfflineError) {
            setError("You're offline. Reconnect to remove them.");
            return;
          }
          if (err instanceof ApiError && err.status === 403) {
            setError("You can't remove them any more.");
            return;
          }
          setError("Couldn't remove them. Try again.");
          // `captureMessage`, not `captureException`, so the report survives
          // `instrument.ts`'s `ignoreErrors` for fetch-failure messages. See
          // `specs/conventions/error-reporting.md`. Metadata only.
          Sentry.captureMessage("Remove issue contributor failed", {
            level: "error",
            tags: { area: "issue-contributors" },
            extra: {
              projectId,
              issueId,
              errorName: err instanceof Error ? err.name : "Unknown",
              status: err instanceof ApiError ? err.status : undefined,
            },
          });
        },
      },
    );
  };

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Remove {name}?</AlertDialogTitle>
        <AlertDialogDescription>{consequence}</AlertDialogDescription>
      </AlertDialogHeader>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
        {/* A plain button, not AlertDialogAction, which would close the dialog
            before the server answers and lose a failure's reason. */}
        <Button
          variant="destructive"
          onClick={handleRemove}
          disabled={remove.isPending}
        >
          {remove.isPending ? "Removing…" : "Remove"}
        </Button>
      </AlertDialogFooter>
    </>
  );
}

/**
 * `6 Oct`, with the year when it isn't this year. Null for a value that
 * doesn't parse.
 */
function formatJoinedDate(iso: string, now: Date): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** "Joined 6 Oct · invited by Rajesh Kumar", or "invited by you". */
function joinedLine(row: IssueContributor, currentUserId: string | undefined) {
  const date = formatJoinedDate(row.joined_at, new Date());
  const joined = date ? `Joined ${date}` : null;
  // A gone account leaves no inviter to name, and the line stops at the date.
  const inviter = row.invited_by
    ? row.invited_by.id === currentUserId
      ? "you"
      : personName(row.invited_by)
    : null;
  if (joined && inviter) return `${joined} · invited by ${inviter}`;
  if (inviter) return `Invited by ${inviter}`;
  return joined ?? "";
}
